import { computed } from 'vue';
import useTrueFitStore from '~/stores/useTrueFitStore';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { loadTrueFit } from '../utils/loadTrueFit';

/**
 * @typedef {Object} tfParams
 * @property {Function} [fitrecProductSuccessCb]
 * @property {Function} [fitrecRegisterOpenCb]
 * @property {Function} [fitrecRegisterCompleteCb]
 * @property {Function} [fitrecHideSkeletonCb]
 * @example
 * import { useTrueFit } from '~/modules/truefit/composables/useTrueFit';
 * const { registerEventListeners } = useTrueFit();
 * registerEventListeners({
 *   fitrecProductSuccessCb: () => console.log('fitrecProductSuccessCb'),
 *   fitrecRegisterOpenCb: () => console.log('fitrecRegisterOpenCb'),
 *   fitrecRegisterCompleteCb: () => console.log('fitrecRegisterCompleteCb'),
 *   fitrecHideSkeletonCb: () => console.log('fitrecHideSkeletonCb'),
 * });
 * */

export const useTrueFit = () => {
  const trueFitStore = useTrueFitStore();

  const lastSelectedSize = computed({
    get() {
      return trueFitStore.lastSelectedSize;
    },
    set(value) {
      trueFitStore.lastSelectedSize = value;
    },
  });

  /**
   *
   * @param {tfParams} param0
   * @returns {void}
   */
  const registerEventListeners = ({ fitrecProductSuccessCb, fitrecRegisterOpenCb, fitrecRegisterCompleteCb, fitrecHideSkeletonCb }) => {
    // @ts-ignore
    const { tfcapi } = window;

    if (!tfcapi) {
      throw new Error('tfcapi is not defined');
    }
    // These events are available when using the tfc-fitrec-product API on a PDP.
    tfcapi('event', 'tfc-fitrec-product', 'success', fitrecProductSuccessCb);
    tfcapi('event', 'tfc-fitrec-product', 'error', fitrecHideSkeletonCb);
    tfcapi('event', 'tfc-fitrec-product', 'render', fitrecHideSkeletonCb);
    // These events are available on any page where the user can launch the True Fit popup.
    tfcapi('event', 'tfc-fitrec-register', 'open', fitrecRegisterOpenCb);
    tfcapi('event', 'tfc-fitrec-register', 'complete', fitrecRegisterCompleteCb);
    tfcapi('event', 'tfc-fitrec-register', 'update', fitrecRegisterCompleteCb);
  };

  /**
   *
   * @param {tfParams} param0
   * @returns {Promise<void>}
   */
  const initTrueFit = async ({ fitrecProductSuccessCb, fitrecRegisterOpenCb, fitrecRegisterCompleteCb, fitrecHideSkeletonCb }) => {
    const { isTrueFitEligible } = useFeatureEligibility();

    if (!isTrueFitEligible) return;

    try {
      await loadTrueFit();
      registerEventListeners({
        fitrecProductSuccessCb,
        fitrecRegisterOpenCb,
        fitrecRegisterCompleteCb,
        fitrecHideSkeletonCb,
      });
    } catch (e) {
      console.error(e);
    }
  };

  return {
    lastSelectedSize,
    initTrueFit,
  };
};

export default useTrueFit;
